import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Skeleton } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import TextInputField from "../common/TextInputField";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import MaskedSecretField from "./MaskedSecretField";
import {
  disconnectInbodyAccount,
  getInbodyAccount,
  InbodyAccount,
  setInbodyAccount,
} from "../../../../common/network/Api";

const EMPTY_ACCOUNT: InbodyAccount = {
  inBodyAPIKey: "",
  inBodyAPIUserName: "",
  inBodyAPIURL: "",
};

const MASK = "•".repeat(11);

// Mask a URL while keeping the scheme and trailing path segment readable,
// e.g. https://apiusa.lookinbody.com/v2 -> https://•••••••••••/v2
const maskUrl = (url: string): string => {
  if (!url) return "";
  const schemeMatch = url.match(/^(https?:\/\/)/i);
  const scheme = schemeMatch ? schemeMatch[1] : "";
  const rest = url.slice(scheme.length);
  const lastSeg = rest.includes("/") ? `/${rest.split("/").pop()}` : "";
  return `${scheme}${MASK}${lastSeg}`;
};

const FIELDS: {
  key: keyof InbodyAccount;
  label: string;
  title: string;
  subtitle: string;
  placeholder: string;
}[] = [
  {
    key: "inBodyAPIKey",
    label: "API Key",
    title: "API Key",
    subtitle: "Your InBody API secret key",
    placeholder: "API Key",
  },
  {
    key: "inBodyAPIUserName",
    label: "Username",
    title: "API Username",
    subtitle: "InBody account username",
    placeholder: "Username",
  },
  {
    key: "inBodyAPIURL",
    label: "URL",
    title: "API URL",
    subtitle: "InBody API endpoint — leave default if unsure",
    placeholder: "URL",
  },
];

const InBodyIntegrationSettings = () => {
  const theme: Theme = useTheme();
  const { AuthUser, AuthSetUser } = useAuth();
  const {
    ASGetUserInfo,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();

  const [connected, setConnected] = useState<boolean>(
    Boolean(AuthUser?.InBodyConnected)
  );
  const [editing, setEditing] = useState<boolean>(false);
  const [form, setForm] = useState<InbodyAccount>(EMPTY_ACCOUNT);
  // Stored auth fields, loaded lazily (only on reveal/edit — never on mount).
  const [account, setAccount] = useState<InbodyAccount | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAccount, setIsLoadingAccount] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const showForm = !connected || editing;
  const isFormValid = FIELDS.every((f) => form[f.key]?.trim());
  // Skeleton the field rows while loading edit values, or while disconnecting.
  // Skeleton the field rows while loading edit values, saving/connecting, or
  // disconnecting.
  const showFieldSkeleton = isLoadingAccount || isSaving;

  // Fetch the stored auth fields on demand (reveal / edit) and cache them.
  const ensureAccountLoaded = async (): Promise<InbodyAccount | null> => {
    if (account) return account;
    try {
      const data = await getInbodyAccount();
      if (!data) return null;
      const loaded: InbodyAccount = {
        inBodyAPIKey: data.inBodyAPIKey ?? "",
        inBodyAPIUserName: data.inBodyAPIUserName ?? "",
        inBodyAPIURL: data.inBodyAPIURL ?? "",
      };
      setAccount(loaded);
      return loaded;
    } catch (error) {
      console.error("Error loading InBody account:", error);
      notify("Error", "Failed to load InBody credentials.", "alert");
      return null;
    }
  };

  const notify = (title: string, message: string, type: string) => {
    ASSetSnackBarMessage({ title, message });
    ASSetTypeSnackBar(type);
    ASSetOpenSnackBar(true);
  };

  const refreshUser = async () => {
    const updatedUser = await ASGetUserInfo(AuthUser);
    await AuthSetUser(updatedUser);
    return Boolean(updatedUser?.InBodyConnected);
  };

  const handleFieldChange = (key: keyof InbodyAccount, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!isFormValid || isSaving) return;
    setIsSaving(true);
    try {
      const payload: InbodyAccount = {
        inBodyAPIKey: form.inBodyAPIKey.trim(),
        inBodyAPIUserName: form.inBodyAPIUserName.trim(),
        inBodyAPIURL: form.inBodyAPIURL.trim(),
      };
      // Store the credentials; the backend verifies the connection on save.
      await setInbodyAccount(payload);

      // The InBodyConnected flag from GetUser is the single source of truth for
      // the connection status — derive the tag/view and the snackbar from it.
      const nowConnected = await refreshUser();
      setConnected(nowConnected);
      setEditing(false);
      setRevealed({});
      // Cache the values so reveal/edit don't need a fetch when showing connected.
      setAccount(nowConnected ? payload : null);

      if (nowConnected) {
        notify(
          "InBody connected",
          "Your InBody account is now connected.",
          "success"
        );
      } else {
        notify(
          "Connection failed",
          "We couldn't verify your InBody account. Please check the entered details and try again.",
          "alert"
        );
      }
    } catch (error) {
      console.error("Error connecting InBody account:", error);
      notify(
        "Couldn't connect to InBody",
        "We couldn't reach InBody with these credentials. Double-check your API key, username, and URL, then try again.",
        "alert"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const performDisconnect = async () => {
    setIsSaving(true);
    try {
      await disconnectInbodyAccount();

      // Source of truth: re-read InBodyConnected from GetUser.
      const nowConnected = await refreshUser();
      setConnected(nowConnected);
      setEditing(false);
      setRevealed({});

      if (!nowConnected) {
        setForm(EMPTY_ACCOUNT);
        setAccount(null);
        notify(
          "InBody disconnected",
          "Your InBody account has been disconnected.",
          "success"
        );
      } else {
        notify("Error", "Failed to disconnect InBody account.", "alert");
      }
    } catch (error) {
      console.error("Error disconnecting InBody account:", error);
      notify("Error", "Failed to disconnect InBody account.", "alert");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = () => {
    setMenuAnchor(null);
    ASSetSnackBarMessage({
      title: "Disconnect InBody",
      message:
        "Are you sure you want to disconnect your InBody account? Body composition sync will stop.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({ onConfirm: performDisconnect });
    ASSetOpenSnackBar(true);
  };

  const handleEdit = async () => {
    setMenuAnchor(null);
    setEditing(true);
    // Values already cached (from a reveal or the connect step) — no fetch.
    if (account) {
      setForm(account);
      return;
    }
    setIsLoadingAccount(true);
    try {
      const loaded = await ensureAccountLoaded();
      setForm(loaded ?? EMPTY_ACCOUNT);
    } finally {
      setIsLoadingAccount(false);
    }
  };

  const handleCancel = () => {
    setForm(account ?? EMPTY_ACCOUNT);
    setEditing(false);
  };

  // Toggling reveal on lazily loads the stored values the first time.
  const toggleReveal = async (key: string) => {
    if (!revealed[key]) {
      const loaded = await ensureAccountLoaded();
      if (!loaded) return;
    }
    setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getDisplayValue = (key: keyof InbodyAccount): string => {
    const value = account?.[key];
    if (revealed[key] && value) return value;
    // Masked placeholder — no stored value needed.
    if (key === "inBodyAPIURL") return value ? maskUrl(value) : `https://${MASK}`;
    return MASK;
  };

  return (
    <Box sx={styles.container(theme)}>
      {/* Header */}
      <Box sx={styles.headerRow}>
        <Box sx={styles.labelSection}>
          <Box sx={styles.titleWrapper(theme)}>InBody integration</Box>
          <Box sx={styles.subtitleWrapper(theme)}>
            Connect your InBody API to enable body composition sync
          </Box>
        </Box>

        <Box sx={styles.statusArea}>
          <Box sx={styles.badge(theme, connected)}>
            {connected ? "Connected" : "Not connected"}
          </Box>
          {connected && !editing && (
            <>
              <Box sx={styles.divider(theme)} />
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ color: theme.colors_ds.NEUTRAL_ICON_DEFAULT }}
                aria-label="InBody integration actions"
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={handleEdit} sx={styles.menuItem}>
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={handleDisconnect}
                  sx={styles.menuItemDanger(theme)}
                >
                  Disconnect
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Fields */}
      {FIELDS.map((field) => (
        <Box key={field.key} sx={styles.fieldRow(theme)}>
          <Box sx={styles.labelSection}>
            <Box sx={styles.titleWrapper(theme)}>{field.title}</Box>
            <Box sx={styles.subtitleWrapper(theme)}>{field.subtitle}</Box>
          </Box>
          <Box sx={styles.controlWrapper}>
            {showFieldSkeleton ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                sx={{ borderRadius: "8px" }}
              />
            ) : showForm ? (
              <TextInputField
                placeholder={field.placeholder}
                value={form[field.key]}
                name={field.key}
                handleChange={(e) =>
                  handleFieldChange(field.key, e.target.value)
                }
              />
            ) : (
              <MaskedSecretField
                label={field.label}
                displayValue={getDisplayValue(field.key)}
                revealed={Boolean(revealed[field.key])}
                onToggleReveal={() => toggleReveal(field.key)}
              />
            )}
          </Box>
        </Box>
      ))}

      {/* Footer actions (form mode) */}
      {showForm && (
        <Box sx={styles.footerRow(theme)}>
          {connected && editing && (
            <SecondaryButton
              label="Cancel"
              onClick={handleCancel}
              isDisable={isSaving}
            />
          )}
          <PrimaryButton
            label="Connect"
            onClick={handleSave}
            isDisable={!isFormValid || isSaving}
            sx={{ justifyContent: "center", width: "auto" }}
            customLabelStyles={{ flex: "none" }}
          />
        </Box>
      )}
    </Box>
  );
};

export default InBodyIntegrationSettings;

const styles = {
  container: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colorTokens.N[50]}`,
    borderRadius: "8px",
    width: "100%",
    display: "flex",
    flexDirection: "column" as "column",
  }),
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
  },
  fieldRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
  }),
  labelSection: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    gap: "4px",
  },
  titleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
  }),
  subtitleWrapper: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
  statusArea: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  badge: (theme: Theme, connected: boolean) => ({
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "19px",
    whiteSpace: "nowrap" as "nowrap",
    color: theme.colorTokens.N[100],
    backgroundColor: connected
      ? theme.colorTokens.S[10]
      : theme.colorTokens.N[30],
    border: `1px solid ${
      connected ? theme.colorTokens.S[70] : theme.colorTokens.N[80]
    }`,
  }),
  divider: (theme: Theme) => ({
    width: "1px",
    height: "27px",
    backgroundColor: theme.colorTokens.N[50],
  }),
  menuItem: {
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  },
  menuItemDanger: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colorTokens.D[70],
  }),
  controlWrapper: {
    width: "438px",
    flexShrink: 0,
  },
  footerRow: (theme: Theme) => ({
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px",
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
  }),
};
